'use client';

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';

interface ServiceTabsProps {
  currentLang?: 'sl' | 'en';
}

export default function ServiceTabs({ currentLang = 'sl' }: ServiceTabsProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeTab, setActiveTab] = useState('apitherapy');
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const tabs = [
    { id: 'apitherapy', label: currentLang === 'sl' ? 'Apiterapija' : 'Apitherapy' },
    { id: 'wax', label: currentLang === 'sl' ? 'Predelava voska' : 'Wax Processing' },
    // { id: 'learning', label: currentLang === 'sl' ? 'Učna pot' : 'Learning Path' },
    // { id: 'accommodation', label: currentLang === 'sl' ? 'Nastanitve' : 'Accommodation' }
  ];

  const content = {
    apitherapy: {
      title: currentLang === 'sl' ? 'Apiterapija' : 'Apitherapy',
      description:
        currentLang === 'sl'
          ? 'Naravno zdravljenje z čebeljimi proizvodi. Masaža z medom, inhalacija z dišečim zrakom iz panja, savna z čebeljim voskom.'
          : 'Natural healing with bee products. Honey massage, inhalation with aromatic air from the hive, sauna with beeswax.',
      benefits:
        currentLang === 'sl'
          ? 'Krepi imunski sistem, zmanjšuje stres, izboljšuje prekrvavitev'
          : 'Strengthens immune system, reduces stress, improves circulation',
      duration: currentLang === 'sl' ? '60-90 minut' : '60-90 minutes',
      availability: currentLang === 'sl' ? 'april – oktober' : 'April – October',
      price: '€40 / ' + (currentLang === 'sl' ? 'oseba' : 'person'),
      images: [
        {
          id: 'img_therapy_1',
          src: '/images/cebelnjak.webp',
          alt:
            currentLang === 'sl'
              ? 'Čebelnjak Čebelarstva Pliberšek s panji in prostorom za apiterapijo'
              : 'Pliberšek apiary house with hives and the apitherapy room',
        },
      ],

      comingSoon: false,
    },
    wax: {
      title: currentLang === 'sl' ? 'Predelava voska' : 'Wax Processing',
      description:
        currentLang === 'sl'
          ? 'Vaš čebelji vosek predelamo v satnice po želenih merah. Satnice in poklopce stopimo v parnem centrifugalnem topilniku.'
          : 'We process your beeswax into foundation sheets in the dimensions you need. Old combs and cappings are melted in a steam centrifugal wax melter.',
      priceList: [
        {
          id: 'wax_foundation',
          label:
            currentLang === 'sl'
              ? 'Predelava voska v satnice po želenih merah'
              : 'Processing wax into foundation sheets of your chosen size',
          price: '2,00 € / kg',
        },
        {
          id: 'wax_sheet',
          label: currentLang === 'sl' ? 'Satnica' : 'Foundation sheet',
          price: currentLang === 'sl' ? '1,50 € / kos' : '1.50 € / piece',
        },
        {
          id: 'wax_melting',
          label:
            currentLang === 'sl'
              ? 'Topljenje satnic (poklopcev) v parnem centrifugalnem topilniku'
              : 'Melting combs (cappings) in a steam centrifugal wax melter',
          price:
            currentLang === 'sl'
              ? '1,00 € / kg pridobljenega voska'
              : '1.00 € / kg of recovered wax',
        },
      ],
      images: [
        {
          id: 'img_wax_1',
          src: '/images/vosek/satnice-stroj.webp',
          alt:
            currentLang === 'sl'
              ? 'Stroj za izdelavo satnic s svežimi satnicami'
              : 'Foundation sheet machine with freshly made sheets',
        },
        {
          id: 'img_wax_2',
          src: '/images/vosek/stopljen-vosek.webp',
          alt:
            currentLang === 'sl'
              ? 'Vedra s stopljenim čebeljim voskom'
              : 'Buckets of melted beeswax',
        },
        {
          id: 'img_wax_3',
          src: '/images/vosek/voscene-plosce.webp',
          alt:
            currentLang === 'sl' ? 'Bloki očiščenega čebeljega voska' : 'Blocks of cleaned beeswax',
        },
      ],

      comingSoon: false,
    },
    learning: {
      title: currentLang === 'sl' ? 'Čebelarska učna pot' : 'Beekeeping Learning Path',
      description:
        currentLang === 'sl'
          ? '3 km dolga čebelarska učna pot skozi naravo. Spoznajte življenje čebel, rastline medonosnice, in tradicionalno čebelarstvo.'
          : '3 km long beekeeping educational path through nature. Learn about bee life, honey plants, and traditional beekeeping.',
      features:
        currentLang === 'sl'
          ? 'Interaktivne točke, QR kode z informacijami, piknik prostor'
          : 'Interactive points, QR codes with information, picnic area',
      status:
        currentLang === 'sl'
          ? 'V pripravi - odprtje pomlad 2026'
          : 'In preparation - opening spring 2026',
      images: [
        {
          id: 'img_path_1',
          src: 'https://images.unsplash.com/photo-1715978018538-7a367526a449',
          alt: 'Forest path through green meadow with wildflowers and trees',
        },
        {
          id: 'img_path_2',
          src: 'https://images.unsplash.com/photo-1493622213392-1cbbc72cb285',
          alt: 'Educational beekeeping sign mockup in natural setting',
        },
      ],

      comingSoon: true,
    },
    accommodation: {
      title: currentLang === 'sl' ? 'Nastanitve' : 'Accommodation',
      description:
        currentLang === 'sl'
          ? 'Apartma in glamp nastanitve sredi narave. Popoln oddih z zajtrkom iz naših izdelkov.'
          : 'Apartment and glamping accommodation in the middle of nature. Perfect relaxation with breakfast from our products.',
      features:
        currentLang === 'sl'
          ? 'Apartma za 4 osebe, Glamp šotor za 2 osebi, Zajtrk vključen'
          : 'Apartment for 4 people, Glamp tent for 2 people, Breakfast included',
      status:
        currentLang === 'sl'
          ? 'V pripravi - odprtje poletje 2026'
          : 'In preparation - opening summer 2026',
      images: [
        {
          id: 'img_accom_1',
          src: 'https://images.unsplash.com/photo-1722248214881-9bc53768965b',
          alt: 'Cozy wooden cabin accommodation in forest setting with mountain view',
        },
        {
          id: 'img_accom_2',
          src: 'https://images.unsplash.com/photo-1718330009439-2a7a1bfa6819',
          alt: 'Glamping tent in meadow surrounded by nature and wildflowers',
        },
      ],

      comingSoon: true,
    },
  };

  const handleTabChange = (tabId: string) => {
    if (tabId === activeTab) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveTab(tabId);
      setIsTransitioning(false);
    }, 300);
  };

  const currentContent = content[activeTab as keyof typeof content];

  if (!isHydrated) {
    return (
      <div className="space-y-12">
        <div className="flex gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 w-40 bg-muted rounded-full animate-pulse"></div>
          ))}
        </div>
        <div className="grid lg:grid-cols-2 gap-12">
          <div className="space-y-4">
            <div className="h-8 bg-muted rounded animate-pulse w-3/4"></div>
            <div className="h-4 bg-muted rounded animate-pulse"></div>
            <div className="h-4 bg-muted rounded animate-pulse w-5/6"></div>
          </div>
          <div className="space-y-6">
            <div className="aspect-video bg-muted rounded-[2rem] animate-pulse"></div>
            <div className="aspect-video bg-muted rounded-[2rem] animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-12">
      {/* Tab Buttons */}
      <div className="flex flex-wrap gap-4">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={`px-8 py-3 rounded-full font-bold text-sm uppercase tracking-widest transition-all duration-300 ${
              activeTab === tab.id
                ? 'bg-primary text-primary-foreground shadow-md'
                : 'bg-muted text-foreground hover:bg-muted/80'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div
        className={`grid lg:grid-cols-2 gap-12 transition-opacity duration-300 ${
          isTransitioning ? 'opacity-0' : 'opacity-100'
        }`}
      >
        {/* Text Content */}
        <div className="space-y-8">
          <div>
            <h3 className="text-4xl font-bold tracking-tighter mb-4">{currentContent.title}</h3>
            {currentContent.comingSoon && (
              <span className="inline-block bg-warning text-warning-foreground px-4 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-4">
                {currentLang === 'sl' ? 'Kmalu' : 'Coming Soon'}
              </span>
            )}
          </div>

          <p className="text-lg text-muted-foreground leading-relaxed">
            {currentContent.description}
          </p>

          {!currentContent.comingSoon && 'benefits' in currentContent && (
            <div className="space-y-2">
              <p className="text-sm font-bold uppercase tracking-widest text-muted-foreground">
                {currentLang === 'sl' ? 'Koristi' : 'Benefits'}
              </p>
              <p className="text-base">{currentContent.benefits}</p>
            </div>
          )}

          {'features' in currentContent && (
            <div className="space-y-2">
              <p className="text-sm font-bold uppercase tracking-widest text-muted-foreground">
                {currentLang === 'sl' ? 'Vključuje' : 'Includes'}
              </p>
              <p className="text-base">{currentContent.features}</p>
            </div>
          )}

          {!currentContent.comingSoon && 'duration' in currentContent && (
            <div className="flex gap-8 pt-4">
              <div className="space-y-1">
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  {currentLang === 'sl' ? 'Trajanje' : 'Duration'}
                </p>
                <p className="text-2xl font-bold">{currentContent.duration}</p>
              </div>
              {'availability' in currentContent && (
                <div className="space-y-1">
                  <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    {currentLang === 'sl' ? 'Na voljo' : 'Available'}
                  </p>
                  <p className="text-2xl font-bold">{currentContent.availability}</p>
                </div>
              )}
              {/* <div className="space-y-1">
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  {currentLang === 'sl' ? 'Cena' : 'Price'}
                </p>
                <p className="text-2xl font-bold text-primary">{currentContent.price}</p>
              </div> */}
            </div>
          )}

          {'priceList' in currentContent && (
            <div className="space-y-4">
              <p className="text-sm font-bold uppercase tracking-widest text-muted-foreground">
                {currentLang === 'sl' ? 'Cenik' : 'Price List'}
              </p>
              <ul className="divide-y divide-border border-y border-border">
                {currentContent.priceList.map((item) => (
                  <li key={item.id} className="flex items-baseline justify-between gap-6 py-4">
                    <span className="text-base">{item.label}</span>
                    <span className="text-lg font-bold text-primary text-right shrink-0 max-w-[11rem]">
                      {item.price}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {currentContent.comingSoon && 'status' in currentContent && (
            <div className="bg-accent/20 border border-accent rounded-lg p-6">
              <p className="text-sm font-medium">{currentContent.status}</p>
            </div>
          )}
        </div>

        {/* Images */}
        <div className={activeTab === 'wax' ? 'grid grid-cols-2 gap-6' : 'space-y-6'}>
          {currentContent.images.map((image, index) => (
            <div
              key={image.id}
              className={`card-rounded overflow-hidden bg-muted ${
                activeTab === 'wax'
                  ? index === 0
                    ? 'col-span-2 aspect-[4/3]'
                    : 'aspect-[4/5]'
                  : 'aspect-video'
              }`}
            >
              <AppImage
                src={image.src}
                alt={image.alt}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
